import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { RadioOptionsWithConfirm, CardContainer, CardWithModal } from 'shared-components';

import { ApplicationState } from '../../../../../store';
import { saveVisaPrepForm } from '../../../../../store/student/actions';
import { createUpdateVPIFieldAction } from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';
import TravelItems from './travelItems';
import { ITravelItems } from '../../../../../types';
import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus } from './utils';
import { useNavigate } from 'react-router-dom';

const VisaPrepFormAddHistoryPage8 = () => {
  // dispatch actions
  const dispatch = useDispatch();
  const onNext = () => dispatch(saveVisaPrepForm(Pages.PAGE2_8));
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(createUpdateVPIFieldAction(fieldName, value, refValue));

  const navigate = useNavigate();
  const onBackToTaskList = () => navigate(-1);

  // state props from store
  const hasTravelled = useSelector(({ student }: ApplicationState) => student.visa.hasTravelled, shallowEqual);

  const defaultArr = useMemo(() => {
    return [];
  }, []);
  const travelItems =
    useSelector(({ student }: ApplicationState) => student.visa.travelItems, shallowEqual) || defaultArr;

  // constants
  const currentStep = 8;
  const steps = 8;

  // component state
  const [complete, setComplete] = useState(false);
  const [travelItemsState, setTravelItemsState] = useState<ITravelItems[]>([]);
  const [showModal, setShowModal] = useState<boolean[]>([]);

  useEffect(() => {
    setTravelItemsState([
      ...travelItems,
      {
        country: '',
        reason: '',
        reasonOther: '',
        entryDate: '',
        exitDate: '',
      },
    ]);
    setShowModal([...travelItems.map(_ => false), false]);
  }, [travelItems]);

  useEffect(() => {
    const { complete: newComplete } = getPageStatus({
      hasTravelled,
      travelItems,
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [complete, hasTravelled, travelItems]);

  const toggleModal = (index: number) => {
    setShowModal(showModal.map((value, i) => (i === index ? !value : value)));
  };

  const deleteTravel = (index: number): void => {
    onChange(VQ.TRAVEL_DETAILS, [...travelItems.slice(0, index), ...travelItems.slice(index + 1)]);
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onNext={onNext}
      onBackToTaskList={onBackToTaskList}
      section={'Additional History'}
    >
      <RadioOptionsWithConfirm
        key={VQ.HAS_TRAVELLED}
        id={VQ.HAS_TRAVELLED}
        label={'Have you ever travelled outside your country of residence, excluding the UK, in the last 10 years?'}
        onClick={(question: any) => {
          onChange(VQ.HAS_TRAVELLED, question.label === 'Yes');
        }}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasTravelled === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: hasTravelled === false,
          },
        ]}
      />

      {hasTravelled === true ? (
        <CardContainer title={`How many times?`}>
          {travelItemsState.map((travel: ITravelItems, index: number) => (
            <CardWithModal
              key={index}
              id={`travel-items-${index}`}
              index={index}
              modalTitle={'Add Travel'}
              cardItems={[
                {
                  label: 'Country',
                  value: travel.country,
                },
                {
                  label: 'Reason for travel',
                  value: travel.reason,
                },
                {
                  label: 'Reason for travel (other)',
                  value: travel.reasonOther,
                },
                {
                  label: 'When did you enter this country?',
                  value: travel.entryDate,
                },
                {
                  label: 'When did you leave this country?',
                  value: travel.exitDate,
                },
              ]}
              onRemove={deleteTravel}
              addButtonLabel={`Add Travel`}
              addAnotherButtonLabel={`I have another Travel to add`}
              hasAddMoreButton={
                travel.country === '' &&
                travel.reason === '' &&
                travel.reasonOther === '' &&
                travel.entryDate === '' &&
                travel.exitDate === ''
              }
              showModal={showModal[index]}
              toggleModal={toggleModal}
            >
              <TravelItems
                index={index}
                country={travel.country}
                reason={travel.reason}
                reasonOther={travel.reasonOther}
                entryDate={travel.entryDate}
                exitDate={travel.exitDate}
                onSaveAndClose={(travel: ITravelItems) => {
                  const travels = [...travelItems];
                  travels[index] = {
                    ...travel,
                  };
                  onChange(VQ.TRAVEL_DETAILS, travels);
                  toggleModal(index);
                }}
              />
            </CardWithModal>
          ))}
        </CardContainer>
      ) : null}
    </QuestionPage>
  );
};
export default VisaPrepFormAddHistoryPage8;
